import { defineStore } from 'pinia';
import { watch } from 'vue';
import { client, getErrorCode, getErrorMessage } from '@/api';
import { ElMessage } from 'element-plus';
import {
  CommentsSortByDto,
  GameCommentDto,
  GameCommentStatusDto,
  PaginatedGameCommentsResponseDto,
  SortDirectionDto,
  UpdateGameCommentRequestDto,
} from '@/api/models';
import { ListGameCommentsParameters } from '@/api/parameters';
import { useUserStore } from '@/store/user';
import { debounce } from 'es-toolkit';
import invariant from 'tiny-invariant';
import { UserProfile } from '@/api/models/user-profile.dto';

export enum ROUTE_STATE {
  UNPUBLISHED = 'unpublished',
  PUBLISHED = 'published',
  SPAM = 'spam',
}

export const COLOR_STATUS: Record<string, string> = {
  new: '#b2b2b2',
  declined: '#f56c6c',
  published: '#67c23a',
  spam: '#e6a23c',
};

export type CommentsState = {
  routeState: ROUTE_STATE | null;

  isWatching: boolean;
  loading: boolean;
  error: string | null;
  onlyNew: boolean;
  search: string | null;
  commentContainsStopPhraseModal: boolean;
  commentConcurrentModificationModal: boolean;

  sortName: CommentsSortByDto | null;
  sortDirection: SortDirectionDto | null;

  comments: GameCommentDto[];
  comment: (GameCommentDto & Partial<UserProfile>) | null;

  pagination: {
    page: number;
    perPage: number;
    total: number;
    pageCount: number;
  };
};

export type ActionName = 'publish' | 'decline' | 'spam';

const DEFAULT_PAGINATION = {
  page: 1,
  perPage: 15,
  total: 0,
  pageCount: 0,
};

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  new: ['publish', 'decline', 'spam'],
  declined: ['publish', 'spam'],
  published: ['decline', 'spam'],
  spam: ['publish', 'decline'],
};

const getInitState = () => ({
  routeState: null,

  commentContainsStopPhraseModal: false,
  commentConcurrentModificationModal: false,
  isWatching: false,
  loading: false,
  error: null,
  onlyNew: false,
  search: null,
  status: [],

  sortName: 'createdAt' as CommentsSortByDto,
  sortDirection: 'DESC' as SortDirectionDto,

  comments: [],
  comment: null,

  pagination: { ...DEFAULT_PAGINATION },
});

export const useComments = defineStore('comments', {
  state: (): CommentsState => getInitState(),

  actions: {
    init() {
      const debounceFetch = debounce(() => {
        if (this.isWatching) {
          this.refresh();
        }
      }, 300);

      watch(
        () => [
          this.onlyNew,
          this.search,
          this.sortName,
          this.sortDirection,
          this.routeState,
        ],
        () => debounceFetch(),
        { immediate: true },
      );
    },

    reset() {
      this.$reset();
    },

    async refresh() {
      this.listGameComments({});
    },

    setRouteState(routeState: ROUTE_STATE) {
      this.routeState = routeState;
    },

    setLoading(value: boolean) {
      this.loading = value;
    },
    setError(value: string | null) {
      this.error = value;
    },
    setComment(comment: (GameCommentDto & UserProfile) | null) {
      this.comment = comment;
    },

    setPagination({ page, limit }: { page: number; limit: number }): void {
      this.pagination = {
        ...this.pagination,
        page,
        perPage: limit,
      };

      this.listGameComments({});
    },

    setSort({ prop, order }: { prop: string; order: string | null }): void {
      if (order === null) {
        this.sortDirection = null;
        this.sortName = null;
      } else {
        this.sortName = prop as CommentsSortByDto;
        this.sortDirection = order === 'ascending' ? 'ASC' : 'DESC';
      }
    },

    async toPublish(comment?: GameCommentDto) {
      const userStore = useUserStore();
      const targetComment = comment ?? this.comment;

      invariant(targetComment, 'Target comment is required.');

      const commentId = Number(targetComment.id);
      const { version } = targetComment;

      invariant(
        version !== undefined,
        `Comment version is required for comment id: ${commentId}`,
      );

      await this.updateGameComment(commentId, {
        moderator: userStore.info.email as string,
        status: 'published',
        version,
      });

      await this.refresh();
    },

    async toDecline(comment?: GameCommentDto) {
      const userStore = useUserStore();
      const targetComment = comment ?? this.comment;

      invariant(targetComment, 'Target comment is required.');

      const commentId = Number(targetComment.id);
      const { version } = targetComment;

      invariant(
        version !== undefined,
        `Comment version is required for comment id: ${commentId}`,
      );

      await this.updateGameComment(commentId, {
        moderator: userStore.info.email as string,
        status: 'declined',
        version,
      });

      await this.refresh();
    },

    async toSpam(comment?: GameCommentDto) {
      const userStore = useUserStore();
      const targetComment = comment ?? this.comment;

      invariant(targetComment, 'Target comment is required.');

      const commentId = Number(targetComment.id);
      const { version } = targetComment;

      invariant(
        version !== undefined,
        `Comment version is required for comment id: ${commentId}`,
      );

      await this.updateGameComment(commentId, {
        moderator: userStore.info.email as string,
        status: 'spam',
        version,
      });

      await this.refresh();
    },

    getStatusByRouteState(): GameCommentStatusDto[] {
      let status: GameCommentStatusDto[] = [];

      if (this.routeState === ROUTE_STATE.UNPUBLISHED) {
        status = this.onlyNew ? ['new'] : ['new', 'declined'];
      } else if (this.routeState === ROUTE_STATE.PUBLISHED) {
        status = ['published'];
      } else if (this.routeState === ROUTE_STATE.SPAM) {
        status = ['spam'];
      }

      return status;
    },

    getStatusColor(status: string): string {
      return COLOR_STATUS[status] ?? 'gray';
    },

    async listGameComments(
      dto: ListGameCommentsParameters,
    ): Promise<PaginatedGameCommentsResponseDto> {
      this.setError(null);
      this.setLoading(true);

      let data: PaginatedGameCommentsResponseDto = {
        items: [],
        total: 0,
        page: 1,
        perPage: 10,
        pageCount: 1,
      };

      try {
        data = await client.commentsApi.listGameComments({
          ...dto,
          status: this.getStatusByRouteState() as GameCommentStatusDto[],
          query:
            this.search && this.search.length >= 3 ? this.search : undefined,

          pageNumber: this.pagination.page,
          pageSize: this.pagination.perPage,

          pageSortBy: this.sortName ? this.sortName : 'createdAt',
          pageSortDirection: this.sortDirection ? this.sortDirection : 'DESC',
        });

        const { items, ...pagination } = data;

        this.comments = items ?? [];
        this.pagination = pagination;
      } catch (err) {
        const message = getErrorMessage(
          err,
          'Error during getting list of comments',
        );

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data;
    },

    async getGameComment(
      id: number,
    ): Promise<(GameCommentDto & UserProfile) | null> {
      this.setError(null);
      this.setLoading(true);

      let comment: (GameCommentDto & UserProfile) | null = null;

      try {
        comment = await client.commentsApi.getGameComment(id);

        this.setComment(comment);
      } catch (err) {
        const message = getErrorMessage(
          err,
          'Error during getting comment complaint',
        );

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }

      return comment;
    },

    async updateGameComment(
      id: number,
      dto: UpdateGameCommentRequestDto,
    ): Promise<(GameCommentDto & UserProfile) | null> {
      this.setError(null);
      this.setLoading(true);

      let comment: (GameCommentDto & UserProfile) | null = null;

      try {
        comment = await client.commentsApi.updateGameComment(id, dto);

        this.setComment(comment);

        ElMessage.success('Comment was updated successfully');
      } catch (err) {
        if (getErrorCode(err) === 1003) {
          this.commentContainsStopPhraseModal = true;
        } else if (getErrorCode(err) === 1004) {
          this.commentConcurrentModificationModal = true;
        } else {
          const message = getErrorMessage(err, 'Error during updating comment');
          ElMessage.error({
            message,
            duration: 10000,
          });
        }
      } finally {
        this.setLoading(false);
      }

      return comment;
    },

    isShowActionButton(
      actionName: ActionName,
      status: GameCommentStatusDto,
    ): boolean {
      return ALLOWED_TRANSITIONS?.[status].includes(actionName) ?? false;
    },
  },
});
